(() => {
  'use strict';
  if (customElements.get('backflow-parts-finder')) return;

  // Field names are live Shopify filter schema, never an embedded product catalog.
  const MAKER = 'filter.p.vendor';
  const SIZE = 'filter.p.m.custom.sizes';
  const MODEL_PREFIX = 'filter.p.m.custom.';
  const modelParam = name => name.startsWith(MODEL_PREFIX) && name.endsWith('_models');
  const previewKeys = ['preview_theme_id', '_fd', 'pb'];
  const isAssemblySize = value => /^(?:\d+(?:\s+\d+\/\d+)?|\d+\/\d+)"$/.test(value.trim());
  const isExactModel = value => value.trim() && !/[,\n\r]/.test(value);
  const fieldNames = maker => {
    const slug = maker.toLowerCase().replace(/\s+/g, '_');
    return [MODEL_PREFIX + slug + '_models', ...(maker === 'APOLLO' ? [MODEL_PREFIX + 'conbraco_models'] : [])];
  };
  const clearFinder = url => {
    [...url.searchParams.keys()].forEach(key => {
      if (key === MAKER || key === SIZE || modelParam(key) || key === 'page' || key === 'section_id') url.searchParams.delete(key);
    });
    return url;
  };

  class PartsFinder extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.form = this.querySelector('[data-finder-form]');
      this.maker = this.querySelector('[data-step="maker"]');
      this.size = this.querySelector('[data-step="size"]');
      this.model = this.querySelector('[data-step="model"]');
      this.submit = this.form.querySelector('button');
      this.status = this.querySelector('[data-status]');
      this.retry = this.querySelector('[data-retry]');
      this.clear = this.querySelector('[data-clear]');
      this.context = new URL(location.href);
      // Shopify can remove the initial preview query after setting its preview cookie.
      // Carry the current draft ID explicitly so filtered links also work when shared.
      if (window.Shopify?.theme?.id && window.Shopify.theme.role !== 'main') {
        this.context.searchParams.set('preview_theme_id', String(window.Shopify.theme.id));
      }
      this.sync = () => { if (this.dataset.lazy !== 'true' || this.closest('details')?.open) this.restore(); };
      this.maker.addEventListener('change', () => {
        this.reset(this.size, 'Loading assembly sizes…');
        this.reset(this.model, 'Choose assembly size first');
        this.update(1);
      });
      this.size.addEventListener('change', () => {
        this.reset(this.model, 'Loading exact models…');
        this.update(2);
      });
      this.model.addEventListener('change', () => this.update(3));
      this.retry.addEventListener('click', () => this.pendingStage ? this.update(this.pendingStage) : this.restore());
      this.form.addEventListener('submit', event => {
        event.preventDefault();
        if (!this.submit.disabled) location.assign(this.selectionURL().href);
      });
      document.addEventListener('collection:reloaded', this.sync);
      window.addEventListener('popstate', this.sync);
      this.preserveLinks();
      const disclosure = this.closest('details');
      if (disclosure && location.hash === '#repair-parts-finder') disclosure.open = true;
      this.openFromHash = () => { if (disclosure && location.hash === '#repair-parts-finder') disclosure.open = true; };
      window.addEventListener('hashchange', this.openFromHash);
      if (this.dataset.lazy === 'true' && disclosure) {
        this.onDisclosureToggle = () => { if (disclosure.open && !this.hasLoaded) this.restore(); };
        disclosure.addEventListener('toggle', this.onDisclosureToggle);
        if (disclosure.open) this.restore();
      } else { this.restore(); }
    }

    disconnectedCallback() {
      this.controller?.abort();
      document.removeEventListener('collection:reloaded', this.sync);
      window.removeEventListener('popstate', this.sync);
      window.removeEventListener('hashchange', this.openFromHash);
      this.closest('details')?.removeEventListener('toggle', this.onDisclosureToggle);
    }

    baseURL() {
      const url = new URL(this.dataset.source, location.origin);
      if (this.dataset.results === 'true') url.search = location.search;
      previewKeys.forEach(key => {
        const value = new URL(location.href).searchParams.get(key) || this.context.searchParams.get(key);
        if (value) url.searchParams.set(key, value);
      });
      return clearFinder(url);
    }

    selectionURL(stage = 3) {
      const url = this.baseURL();
      if (this.maker.value) url.searchParams.set(MAKER, this.maker.value);
      if (stage >= 2 && this.size.value) url.searchParams.set(SIZE, this.size.value);
      const option = this.model.selectedOptions[0];
      if (stage >= 3 && option?.dataset.param && option.value) url.searchParams.set(option.dataset.param, option.value);
      return url;
    }

    preserveLinks() {
      const scope = this.closest('.parts-finder-scope') || this.closest('section');
      scope.querySelectorAll('a').forEach(link => {
        const url = new URL(link.href);
        if (url.origin !== location.origin) return;
        previewKeys.forEach(key => {
          if (this.context.searchParams.has(key)) url.searchParams.set(key, this.context.searchParams.get(key));
        });
        link.href = url.href;
      });
      const search = scope.querySelector('[data-parts-search]');
      previewKeys.forEach(key => {
        if (!search || !this.context.searchParams.has(key)) return;
        const input = document.createElement('input');
        input.type = 'hidden'; input.name = key; input.value = this.context.searchParams.get(key);
        search.append(input);
      });
      this.clear.href = this.baseURL().href;
    }

    reset(select, label) {
      select.replaceChildren(new Option(label, ''));
      select.disabled = true;
      this.submit.disabled = true;
    }

    fill(select, values, placeholder, selected = null) {
      select.replaceChildren(new Option(placeholder, ''));
      values.forEach(value => {
        const option = new Option(value.label, value.value);
        if (value.param) option.dataset.param = value.param;
        if (selected && value.value === selected.value && (!selected.param || selected.param === value.param)) option.selected = true;
        select.append(option);
      });
      if (selected?.value && ![...select.options].some(option => option.selected && option.value)) {
        const option = new Option(selected.value + ' (no listed matches)', selected.value, false, true);
        if (selected.param) option.dataset.param = selected.param;
        option.disabled = true;
        select.append(option);
      }
      select.disabled = values.length === 0 && !selected?.value;
    }

    start() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.submit.disabled = true;
      this.retry.hidden = true;
      this.querySelector('[data-share]').hidden = true;
      this.setAttribute('aria-busy', 'true');
      this.status.textContent = 'Loading live repair-parts choices…';
      return this.controller;
    }

    async load(url, controller) {
      url.searchParams.set('section_id', 'parts-finder-data');
      const timeout = setTimeout(() => controller.abort(), 15000);
      try {
        const response = await fetch(url.href, {signal: controller.signal, credentials: 'same-origin'});
        if (!response.ok) throw new Error('request');
        const document = new DOMParser().parseFromString(await response.text(), 'text/html');
        const template = document.querySelector('template[data-parts-finder-data]');
        if (!template) throw new Error('missing');
        return {
          count: Number(template.dataset.count),
          filters: [...template.content.querySelectorAll('[data-filter]')].map(filter => ({
            param: filter.dataset.param,
            label: filter.dataset.label,
            values: [...filter.querySelectorAll('[data-value]')].map(value => ({value: value.dataset.value, label: value.textContent, count: Number(value.dataset.count)}))
          }))
        };
      } finally { clearTimeout(timeout); }
    }

    values(data, param) {
      return data.filters.find(filter => filter.param === param)?.values.filter(value => value.count > 0) || [];
    }

    models(data) {
      const wanted = fieldNames(this.maker.value);
      const fields = data.filters.filter(filter => wanted.includes(filter.param));
      return fields.flatMap(filter => filter.values.filter(value => value.count > 0 && isExactModel(value.value)).map(value => ({
        ...value, param: filter.param,
        label: value.label + (fields.length > 1 ? ' · ' + filter.label.replace(/ Models$/, '') : '')
      })));
    }

    async restore() {
      this.hasLoaded = true;
      this.pendingStage = null;
      const params = new URL(location.href).searchParams;
      const maker = params.getAll(MAKER);
      const sizes = params.getAll(SIZE);
      const models = [...params].filter(([key]) => modelParam(key));
      const multiple = maker.length > 1 || sizes.length > 1 || models.length > 1;
      if (this.dataset.results === 'true' && (maker.length || sizes.length || models.length)) { const disclosure = this.closest('details'); if (disclosure) disclosure.open = true; }
      this.reset(this.maker, 'Loading manufacturers…');
      this.reset(this.size, 'Choose manufacturer first');
      this.reset(this.model, 'Choose assembly size first');
      const controller = this.start();
      try {
        const baseURL = this.baseURL();
        const makerURL = new URL(baseURL);
        if (maker[0]) makerURL.searchParams.set(MAKER, maker[0]);
        const sizeURL = new URL(makerURL);
        if (sizes[0]) sizeURL.searchParams.set(SIZE, sizes[0]);
        const matchURL = new URL(sizeURL);
        if (models[0]) matchURL.searchParams.set(models[0][0], models[0][1]);
        // These context requests are independent; restore shared URLs in one round trip.
        const [base, byMaker, bySize, matches] = await Promise.all([
          this.load(baseURL, controller),
          !multiple && maker[0] ? this.load(makerURL, controller) : null,
          !multiple && maker[0] && sizes[0] ? this.load(sizeURL, controller) : null,
          !multiple && maker[0] && sizes[0] && models[0] ? this.load(matchURL, controller) : null
        ]);
        if (controller !== this.controller) return;
        if (!base.filters.some(filter => filter.param === MAKER) || !base.filters.some(filter => filter.param === SIZE)) throw new Error('missing');
        this.fill(this.maker, this.values(base, MAKER), 'Choose manufacturer', !multiple && maker[0] ? {value: maker[0]} : null);
        if (multiple) {
          this.clear.hidden = false;
          this.status.textContent = 'Multiple filters are selected below. Choose one assembly here to start a guided match, or use the existing collection filters.';
          return;
        }
        if (!maker[0]) { this.status.textContent = 'Choose your assembly’s manufacturer to begin.'; return; }
        this.fill(this.size, this.values(byMaker, SIZE).filter(value => isAssemblySize(value.value)), 'Choose assembly size', sizes[0] ? {value: sizes[0]} : null);
        if (!sizes[0]) { this.status.textContent = this.size.disabled ? 'No assembly sizes are listed for this manufacturer. Browse or search by part number.' : 'Choose the size marked on your assembly.'; return; }
        this.fill(this.model, this.models(bySize), 'Choose exact model', models[0] ? {param: models[0][0], value: models[0][1]} : null);
        if (!models[0]) { this.status.textContent = this.model.disabled ? 'No exact models are listed for this size. Try another size, browse, or search.' : 'Choose the full model, including its suffix.'; return; }
        this.finish(matches.count, true);
      } catch (error) { this.failed(controller, error); }
      finally { if (controller === this.controller) { this.removeAttribute('aria-busy'); this.clear.hidden = ![...params.keys()].some(key => key === MAKER || key === SIZE || modelParam(key)); } }
    }

    async update(stage) {
      this.pendingStage = stage;
      const controller = this.start();
      try {
        if (!this.maker.value) {
          this.reset(this.size, 'Choose manufacturer first');
          this.reset(this.model, 'Choose assembly size first');
          this.status.textContent = 'Choose your assembly’s manufacturer to begin.';
          return;
        }
        if (stage === 1) {
          this.maker.disabled = true;
          const data = await this.load(this.selectionURL(1), controller);
          if (controller !== this.controller) return;
          this.fill(this.size, this.values(data, SIZE).filter(value => isAssemblySize(value.value)), 'Choose assembly size');
          this.status.textContent = this.size.disabled ? 'No assembly sizes are listed for this manufacturer. Browse or search by part number.' : 'Choose the size marked on your assembly.';
        } else if (stage === 2) {
          if (!this.size.value) { this.reset(this.model, 'Choose assembly size first'); this.status.textContent = 'Choose the size marked on your assembly.'; return; }
          this.size.disabled = true;
          const data = await this.load(this.selectionURL(2), controller);
          if (controller !== this.controller) return;
          this.fill(this.model, this.models(data), 'Choose exact model');
          this.status.textContent = this.model.disabled ? 'No exact models are listed for this size. Try another size, browse, or search.' : 'Choose the full model, including its suffix.';
        } else if (this.model.value) {
          this.model.disabled = true;
          const data = await this.load(this.selectionURL(), controller);
          if (controller !== this.controller) return;
          this.finish(data.count);
        } else { this.status.textContent = 'Choose the full model, including its suffix.'; }
      } catch (error) { this.failed(controller, error); }
      finally {
        if (controller === this.controller) {
          this.removeAttribute('aria-busy');
          this.maker.disabled = this.maker.options.length <= 1;
          this.size.disabled = this.size.options.length <= 1;
          this.model.disabled = this.model.options.length <= 1;
          this.clear.hidden = !this.maker.value;
          this.clear.href = this.baseURL().href;
        }
      }
    }

    finish(count, restored = false) {
      const valid = [this.maker, this.size, this.model].every(select => select.value && !select.selectedOptions[0]?.disabled);
      this.submit.disabled = !valid || !count;
      const next = restored && this.dataset.results === 'true' ? 'Results are shown below.' : 'Select “Find repair parts” to view results.';
      this.status.textContent = count && valid ? `${count} catalog-listed ${count === 1 ? 'match' : 'matches'}. ${next}` : 'No catalog-listed matches for these selections. Change your assembly selection, browse all repair parts, or search by part number.';
      const share = this.querySelector('[data-share]');
      share.href = this.selectionURL().href;
      share.hidden = !valid;
    }

    failed(controller, error) {
      if (controller !== this.controller) return;
      this.submit.disabled = true;
      this.status.textContent = error.message === 'missing' ? 'Required live filters are unavailable. Browse the repair-parts collection or search by part number.' : 'Couldn’t load live choices. Try again, browse repair parts, or use search.';
      this.retry.hidden = false;
    }
  }
  customElements.define('backflow-parts-finder', PartsFinder);
  document.addEventListener('shopify:section:load', () => {
    document.querySelectorAll('backflow-parts-finder').forEach(finder => finder.connectedCallback());
  });
})();
